/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import {
  AbsoluteCenter,
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  HStack,
  Icon,
  Input,
  Skeleton,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { ReportObject, ReportQueryParameter } from '@karya/core';
import { createColumnHelper, PaginationState } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { HiArrowPath } from 'react-icons/hi2';
import { useLocation, useParams } from 'react-router-dom';
import { AdvancedTable } from '@/components/AdvancedTable';
import { FilePreview } from '@/components/Reports/FilePreview';
import { ReportKind, reportsApi, RequestSource } from '@/features/reportsApi';
import { ButtonVariant } from '@/themes/ButtonVariants';
import GranularSearch, { Filter } from '../Utils/GranularSearch';
import { ReportComponenetPreview, ReportComponenetPreviewMeta } from './ComponentPreview';
import { useRef } from 'react';
import { DownloadCSV } from './DownloadReportCSV';

type PageParams = {
  kind?: ReportKind;
  id: string;
};

type Props = Partial<PageParams> & {
  src: RequestSource; // Src to pass in the headers
};

const { useGetReportResultQuery, useGetReportDownloadUrlQuery } = reportsApi;

const columnHelper = createColumnHelper<Record<string, unknown>>();

const renderTableCellValue = (value: unknown) => {
  if (value == null) {
    return '';
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return value;
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  // Avoid rendering raw objects/arrays as React children.
  if (Array.isArray(value) || typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

const normalizeReportQueryParameters = (rawValue: unknown): ReportQueryParameter[] => {
  if (Array.isArray(rawValue)) {
    return rawValue as ReportQueryParameter[];
  }

  if (rawValue && typeof rawValue === 'object') {
    const nested = (rawValue as { query_parameters?: unknown }).query_parameters;
    if (Array.isArray(nested)) {
      return nested as ReportQueryParameter[];
    }

    // Defensive handling for accidental double nesting from intermediate transforms.
    if (nested && typeof nested === 'object') {
      const doubleNested = (nested as { query_parameters?: unknown }).query_parameters;
      if (Array.isArray(doubleNested)) {
        return doubleNested as ReportQueryParameter[];
      }
    }
  }

  return [];
};

export const Report = ({ id: reportId, src }: Props) => {
  // This ref will store if the first update has occurred
  const reportParamsFirstChange = useRef(true);
  const pageParams = useParams<PageParams>();
  const id = reportId || pageParams.id;

  const {
    data: reportData,
    isError,
    error,
  } = reportsApi.useGetReportByIdQuery(
    { id, src },
    {
      skip: !id,
    },
  );
  const [reportParams, setReportParams] = useState<NonNullable<ReportObject['params']>>([]);
  const [paramList, setParamList] = useState<string[]>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const [mediaEnabled, setMediaEnabled] = useState(false);
  const [searchQuery, setSearchQuery] = useState<Record<string, string>>({});
  const [filters, setFilters] = useState<Filter[]>([]);
  const [forceRefresh, setForceRefresh] = useState(false);

  // See if we already have search params in the URL
  const location = useLocation();

  const { reportResult, isSuccess, isFetching } = useGetReportResultQuery(
    {
      id,
      limit: pagination.pageSize,
      page: pagination.pageIndex,
      params: paramList,
      searchQuery: Object.keys(searchQuery).length > 0 ? searchQuery : undefined,
      forceRefresh,
      src,
    },
    {
      // Skip the query if the report is dynamic and no params are provided
      skip: !id || !reportData || (reportData.type === 'dynamic' && paramList.length === 0),
      selectFromResult: ({ data, isSuccess, isFetching }) => {
        return {
          reportResult: data,
          isSuccess,
          isFetching,
        };
      },
    },
  );

  const tableColumns = useMemo(() => {
    if (reportData && reportResult?.meta.columns) {
      return reportResult.meta.columns;
    } else {
      return [];
    }
  }, [reportData, reportResult?.meta.columns]);

  const categories = useMemo(() => {
    return tableColumns.map((column) => {
      return {
        id: column,
        displayString: column.replace(/_/g, ' '),
      };
    });
  }, [tableColumns]);

  const setGranularFilters = useCallback(
    (filters: Filter[]) => {
      const searchQuery: Record<string, string> = {};
      tableColumns.forEach((column) => {
        searchQuery[column] = '';
      });
      filters.forEach((filter) => {
        searchQuery[filter.category.id] = filter.input;
      });
      setSearchQuery(searchQuery);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [tableColumns],
  );

  const { downloadUrl, isUrlError, isUrlFetching } = useGetReportDownloadUrlQuery(
    {
      id,
      params: paramList,
      mediaEnabled,
      searchQuery: Object.keys(searchQuery).length > 0 ? searchQuery : undefined,
      forceRefresh,
      src,
    },
    {
      // Skip the query if the report is dynamic and no params are provided
      skip: !id || !reportData || (reportData.type === 'dynamic' && paramList.length === 0),
      selectFromResult: ({ data, isFetching, isError }) => {
        return {
          downloadUrl: data ? data.url : '',
          isUrlFetching: isFetching,
          isUrlError: isError,
        };
      },
    },
  );

  // Reset the reports state when the report changes
  useEffect(() => {
    setMediaEnabled(false);
    setParamList([]);
    setMediaEnabled(false);
    setFilters([]);
    setGranularFilters([]);
    setForceRefresh(false);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    // This hook should only run when report id changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Set the report params when the report data is fetched
  useEffect(() => {
    if (!reportData) {
      return;
    }
    const rawParams = normalizeReportQueryParameters(reportData.config?.query_parameters);
    const params = rawParams.map((param) => {
      return { ...param, value: param.value ?? '' };
    });
    setReportParams(params);
  }, [reportData]);

  // Extract all query parameters as key-value pairs while filtering out the 'paramlist' keys
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (reportParams.length > 0) {
      const newParams = [...reportParams];
      reportParams.forEach((param, index) => {
        if (queryParams.has(param.name)) {
          newParams[index].value = queryParams.get(param.name) ?? '';
        }
      });
      // Check if this is the first change of reportParams
      if (reportParamsFirstChange.current) {
        setReportParams(newParams);
        reportParamsFirstChange.current = false;
        // Set to false after the first change
      }
    }
  }, [reportParams, location.search]);

  if (!id) {
    return <div>Invalid report</div>;
  }

  if (isError) {
    // Display error message
    return (
      <AbsoluteCenter w="100%">
        <VStack>
          <Heading>
            {/*  @ts-expect-error -- error object typing is not inferred correctly */}
            {error.status}: {error.data.title}
          </Heading>
          <Text fontSize="xl" fontWeight="medium">
            {/*  @ts-expect-error -- error object typing is not inferred correctly */}
            {error.data.messages[0]}
          </Text>
        </VStack>
      </AbsoluteCenter>
    );
  }

  return (
    <Box pb={16}>
      <Heading fontSize={{ base: 'xl', md: '2xl' }} mx={{ base: 6, md: 12 }} mt={{ base: 6, md: 12 }} mb={4}>
        {reportData?.name}
      </Heading>
      {reportParams.length > 0 && (
        <Box bg="gray.100" px={{ base: 6, md: 12 }} mb={8} mt={4} py={8}>
          <Heading fontSize={{ base: 'md', md: 'lg' }} mb={{ base: 4, md: 8 }}>
            Parameters
          </Heading>
          {reportParams.map((param, index: number) => (
            <Stack mt={2} key={param.name} width="100%" spacing={1}>
              <Text fontSize="sm" fontWeight="bold">
                {param.name}
              </Text>
              <Flex alignItems="center" gap={2}>
                <Input
                  flexShrink={0}
                  size="sm"
                  width={{ base: '100%', md: '40%' }}
                  placeholder={param.name}
                  background="white"
                  value={param.value as string}
                  onChange={(e) => {
                    const newParams = [...reportParams];
                    newParams[index].value = e.target.value;
                    setReportParams(newParams);
                  }}
                />
                <Text fontSize="sm" color="gray.700">
                  {param.comments}
                </Text>
              </Flex>
            </Stack>
          ))}
          <Button
            colorScheme="green"
            size={{ base: 'sm', md: 'md' }}
            mt={6}
            mb={8}
            onClick={() => {
              setParamList(reportParams.map((param) => param.value as string));
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
          >
            Generate Report
          </Button>
        </Box>
      )}

      <Box mx={{ base: 6, md: 12 }}>
        <VStack align="start" width="100%">
          <Stack width="50%">
            <Button
              size="sm"
              alignSelf="start"
              leftIcon={<Icon as={HiArrowPath} />}
              variant={ButtonVariant.submit}
              isDisabled={isFetching || !isSuccess}
              onClick={() => {
                setForceRefresh(true);
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
            >
              Refresh Data
            </Button>
            <GranularSearch
              categories={categories}
              setAppliedFilters={setGranularFilters}
              filters={filters}
              setFilters={setFilters}
            />
          </Stack>
          {isFetching ? (
            <Stack width="100%">
              <Skeleton height="20px" width="100%" />
              <Skeleton height="20px" width="100%" />
              <Skeleton height="20px" width="100%" />
            </Stack>
          ) : isSuccess && reportResult ? (
            reportResult.data.length > 0 && reportResult.meta.id === id ? (
              <>
                <AdvancedTable
                  data={reportResult.data}
                  columns={tableColumns.map((column: string) =>
                    columnHelper.accessor(column, {
                      header: `${column.replace(/_/g, ' ')}`,
                      cell: ({ row }) => {
                        const value = row.getValue(column);
                        if (typeof value === 'string' && value.startsWith('https://')) {
                          return <FilePreview url={value} />;
                        }
                        if (value && typeof value === 'object' && 'type' in value && value.type === '__component__') {
                          const previewMeta = value as ReportComponenetPreviewMeta;
                          return <ReportComponenetPreview {...previewMeta} />;
                        } else {
                          return renderTableCellValue(value);
                        }
                      },
                    }),
                  )}
                  pageSize={pagination.pageSize}
                  pageCount={Math.ceil(reportResult.pagination.totalCount / pagination.pageSize)}
                  paginationState={pagination}
                  onPaginationChange={setPagination}
                  manualPagination
                  autoResetPageIndex={false}
                  pageSizeOptions={[10, 20, 50]}
                  onPageSizeChange={(newSize) => {
                    setPagination({ pageIndex: 0, pageSize: newSize });
                  }}
                />
                <HStack mt={8}>
                  <DownloadCSV url={downloadUrl} isLoading={isUrlFetching} isError={isUrlError} />
                  <Checkbox
                    isChecked={mediaEnabled}
                    onChange={(e) => {
                      setMediaEnabled(e.target.checked);
                    }}
                  >
                    Download with media
                  </Checkbox>
                </HStack>
              </>
            ) : (
              <Text>Nothing to show.</Text>
            )
          ) : (
            <Text fontWeight="medium">No data found.</Text>
          )}
        </VStack>
      </Box>
    </Box>
  );
};
